
import { DEFAULT_CUT_SETTINGS } from '../../types/api';
import type { CutCandidate, CutSettings } from '../../types/api';

export function verdictTone(score: number): 'ok' | 'warn' {
  return score >= 0.6 ? 'ok' : 'warn';
}

export function plural(count: number, forms: [string, string, string]): string {
  const tail = count % 10;
  const hundreds = count % 100;
  if (tail === 1 && hundreds !== 11) return forms[0];
  if (tail >= 2 && tail <= 4 && (hundreds < 12 || hundreds > 14)) return forms[1];
  return forms[2];
}

export function timecode(seconds: number): string {
  const safe = Math.max(0, seconds);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = Math.floor(safe % 60);
  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function durationLabel(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)} с`;
  return timecode(seconds);
}

export function megabytes(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(1)} ГБ`;
  return `${Math.round(bytes / 1024 ** 2)} МБ`;
}

export function candidateFootnote(candidate: CutCandidate): string {
  return `${candidate.metrics.frames} кадр. · ±${candidate.metrics.yaw_spread.toFixed(0)}°`;
}

export function isDefaultSettings(settings: CutSettings): boolean {
  return (
    settings.max_yaw_deg === DEFAULT_CUT_SETTINGS.max_yaw_deg &&
    settings.ideal_yaw_deg === DEFAULT_CUT_SETTINGS.ideal_yaw_deg &&
    settings.max_pitch_deg === DEFAULT_CUT_SETTINGS.max_pitch_deg &&
    settings.ideal_pitch_deg === DEFAULT_CUT_SETTINGS.ideal_pitch_deg &&
    settings.min_face_area_frac === DEFAULT_CUT_SETTINGS.min_face_area_frac &&
    settings.min_duration_s === DEFAULT_CUT_SETTINGS.min_duration_s
  );
}

export function canRelax(settings: CutSettings): boolean {
  return (
    settings.max_yaw_deg < 45 ||
    settings.max_pitch_deg < 45 ||
    settings.min_face_area_frac > 0.005
  );
}

export function relaxed(settings: CutSettings): CutSettings {
  const maxYaw = Math.min(45, Math.round(settings.max_yaw_deg * 1.4));
  const maxPitch = Math.min(45, Math.round(settings.max_pitch_deg * 1.4));
  return {
    max_yaw_deg: maxYaw,
    ideal_yaw_deg: Math.min(settings.ideal_yaw_deg, maxYaw),
    max_pitch_deg: maxPitch,
    ideal_pitch_deg: Math.min(settings.ideal_pitch_deg, maxPitch),
    min_face_area_frac: Math.max(0.005, settings.min_face_area_frac / 2),
    min_duration_s: settings.min_duration_s,
  };
}
